import { useContext, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useLocation, useParams } from "react-router-dom";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { Person } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import format from "date-fns/format";

import { ReactComponent as Task } from "assets/icons/activity/task.svg";

import { ButtonContained } from "components/button/ButtonContained";

import { TaskActivityResponse } from "user-journey/consts/journeyActivity";
import { ActivityStatus } from "user-journey/consts/journeyActivity";

import { apiClient } from "utils/apiClient";
import { getErrorMessage } from "utils/errorUtils";
import { UserLevelContext } from "utils/contexts/user-level";
import { stripHtmlTags } from "utils/string-utils";

import { getToken } from "auth/helpers/authApi";

import { useIsTruncated } from "hooks/useIsTruncated";

import {
	ActivityCard,
	ActivityHeader,
	ActivityStatusChip,
	buttonColor,
	CalendarIcon,
	LockedSection,
} from "../JourneyActivity.styled";
import { TaskActivityDetails } from "user-journey/components/TaskActivityDetails";

export const TaskActivityCard = ({
	activity,
	fetchUserJourney,
	refetchExternalTasks,
}: {
	activity: TaskActivityResponse;
	fetchUserJourney?: () => Promise<void>;
	refetchExternalTasks?: () => Promise<void>;
}) => {
	const { pathname } = useLocation();
	const params = useParams();

	const { formatMessage } = useIntl();

	const { enqueueSnackbar } = useSnackbar();

	const theme = useTheme();

	const { fetchUserLevel } = useContext(UserLevelContext);
	const [completeLoading, setCompleteLoading] = useState(false);
	const [openDetails, setOpenDetails] = useState(false);
	const messageRef = useRef<HTMLDivElement>(null);

	const isTruncated = useIsTruncated(messageRef, [activity.description, activity.status]);

	const isExternal = pathname.startsWith("/external");

	const completeHandler = async () => {
		const token = getToken();
		setCompleteLoading(true);

		try {
			const url = isExternal
				? `user-tasks/external/${params.base64Email}/complete/${activity.userTaskId}`
				: `user-tasks/${activity.userTaskId}/complete`;
			await apiClient(url, {
				token,
				method: "POST",
			});

			if (fetchUserJourney && !isExternal) {
				await fetchUserJourney();
			}
			if (isExternal && refetchExternalTasks) {
				await refetchExternalTasks();
			}

			setOpenDetails(false);
			enqueueSnackbar(
				formatMessage({
					id: "DASHBOARD.employee-journey.activity_type.task.complete.success",
					defaultMessage: "Task completed",
				}),
				{
					variant: "success",
				},
			);

			if (!isExternal) {
				await fetchUserLevel();
			}
		} catch (error) {
			const errorJson = (error as any)?.json;
			let errorMessage = formatMessage({
				id: "DASHBOARD.employee-journey.create_error",
			});
			if (errorJson.code) {
				errorMessage = getErrorMessage(errorJson.code);
			}
			enqueueSnackbar(errorMessage, { variant: "error" });
		} finally {
			setCompleteLoading(false);
		}
	};

	return (
		<ActivityCard status={activity.status} className="TaskActivityCard" sx={{ justifyContent: "space-between" }}>
			<TaskActivityDetails
				open={openDetails}
				activity={activity}
				onClose={() => setOpenDetails(false)}
				onContinue={completeHandler}
				loading={completeLoading}
			/>

			<ActivityStatusChip status={activity.status} />

			<Stack gap={3}>
				<ActivityHeader>
					<Task />

					<Typography variant="md" title={activity.title}>
						{activity.title}
					</Typography>
				</ActivityHeader>

				{["calendar", "user"].map(
					(row) =>
						((!!activity.user && row === "user") || row === "calendar") && (
							<Stack key={row} direction="row" alignItems="center" justifyContent="space-between" gap={2}>
								<Stack direction="row" alignItems="center" gap={1}>
									{row === "calendar" ? <CalendarIcon /> : <Person />}

									<Typography component="span" variant="sm">
										{formatMessage({
											id: row === "calendar" ? "GLOBAL.date" : "AUTH.login.input.name.label",
										})}
									</Typography>
								</Stack>

								<Typography component="strong" variant="sm" flex="1" textAlign="right" fontWeight={700}>
									{row === "calendar"
										? format(new Date(activity.dueDate), "MMM dd, yyyy")
										: activity.user?.name}
								</Typography>
							</Stack>
						),
				)}

				{activity.status !== ActivityStatus.LOCKED && (
					<Box
						title={stripHtmlTags(activity.description as string)}
						sx={{
							width: "100%",
							...theme.typography.sm,
							color: "dirtyWhite.2000",
							textOverflow: "ellipsis",
							overflow: "hidden",

							"& .communication-message": {
								display: "-webkit-box",
								WebkitLineClamp: isExternal ? 1 : 2,
								WebkitBoxOrient: "vertical",
								overflow: "hidden",
								textOverflow: "ellipsis",

								"& *": {
									color: "inherit !important",
								},
							},
							"& .custom-variable": {
								fontWeight: 700,
							},
							[theme.breakpoints.down("sm")]: {
								...(isExternal && {
									display: "none",
								}),
							},
						}}
					>
						<div
							ref={messageRef}
							className="communication-message"
							dangerouslySetInnerHTML={{
								__html: activity.description as string,
							}}
						/>
					</Box>
				)}
			</Stack>

			{activity.status === ActivityStatus.LOCKED ? (
				<LockedSection activity={activity} />
			) : (
				<Stack gap={{ sm: isExternal ? 0 : 4 }}>
					{(isTruncated || isExternal) && (
						<Box py={1.5}>
							<Typography
								textAlign="center"
								mx="auto"
								variant="sm"
								fontWeight={600}
								textTransform="capitalize"
								sx={{
									cursor: "pointer",
									width: "fit-content",
									borderBottom: "1px solid",
									borderColor: "dirtyWhite.2000",
									transition: "all 0.3s ease-in-out",

									"&:hover": {
										borderColor: "transparent",
									},
								}}
								role="button"
								onClick={() => setOpenDetails(true)}
							>
								{formatMessage({
									id: "GLOBAL.view-details",
								})}
							</Typography>
						</Box>
					)}

					{activity.status === ActivityStatus.COMPLETED ? (
						<Stack
							alignItems="center"
							justifyContent="center"
							px={4}
							py={2}
							height={theme.spacing(10)}
							border="2px dashed"
							borderColor="success.main"
							borderRadius={theme.spacing(2)}
						>
							<Typography
								color="success.main"
								textAlign="center"
								variant="md"
								fontWeight={800}
								lineHeight={1}
							>
								{formatMessage({
									id: "USER-JOURNEY.activity.card.status.completed",
								})}{" "}
								🎉
							</Typography>
						</Stack>
					) : (
						<ButtonContained
							fullWidth
							color={buttonColor[activity.status]}
							onClick={completeHandler}
							disabled={completeLoading}
							loading={completeLoading}
						>
							{formatMessage({
								id: "DASHBOARD.employee-journey.activity_type.task.request-completion",
								defaultMessage: "Complete Task",
							})}
						</ButtonContained>
					)}
				</Stack>
			)}
		</ActivityCard>
	);
};
